/**
 * Keycloak / OIDC client (Authorization Code + PKCE).
 *
 * Uses oidc-client-ts UserManager against the realm issuer configured via
 * VITE_OIDC_AUTHORITY and VITE_OIDC_CLIENT_ID. Access tokens are Keycloak
 * tokens — the nbcallback API validates them and returns app roles from
 * GET /auth/me (shared with bridge-mapview).
 */

import { User, UserManager, WebStorageStateStore } from "oidc-client-ts";

let userManager: UserManager | null = null;

function requireEnv(name: string): string {
  const value = import.meta.env[name];
  if (!value || !String(value).trim()) {
    throw new Error(`${name} is not configured.`);
  }
  return String(value).trim();
}

/** Build (once) the shared UserManager for this origin. */
export function getUserManager(): UserManager {
  if (userManager) return userManager;

  const authority = requireEnv("VITE_OIDC_AUTHORITY").replace(/\/$/, "");
  const clientId = requireEnv("VITE_OIDC_CLIENT_ID");
  const scope =
    (import.meta.env.VITE_OIDC_SCOPE &&
      String(import.meta.env.VITE_OIDC_SCOPE).trim()) ||
    "openid profile email offline_access";

  const origin = window.location.origin;

  userManager = new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: `${origin}/auth/callback`,
    post_logout_redirect_uri: `${origin}/login`,
    response_type: "code",
    scope,
    automaticSilentRenew: false,
    monitorSession: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
  });

  return userManager;
}

/** Start the browser redirect to the Keycloak login UI. */
export async function beginLogin(): Promise<void> {
  await getUserManager().signinRedirect();
}

/** Finish the redirect login (call from /auth/callback). */
export async function completeLogin(): Promise<User> {
  return getUserManager().signinRedirectCallback();
}

/** Load any persisted OIDC user (may be expired). */
export async function getStoredUser(): Promise<User | null> {
  try {
    return await getUserManager().getUser();
  } catch {
    return null;
  }
}

/** Renew tokens via refresh_token grant. */
export async function renewUser(): Promise<User> {
  const user = await getUserManager().signinSilent();
  if (!user) {
    throw new Error("Silent renew returned no user");
  }
  return user;
}

/** Clear the local OIDC user record without hitting Keycloak. */
export async function clearStoredUser(): Promise<void> {
  try {
    await getUserManager().removeUser();
  } catch {
    // best-effort
  }
}

/** End the SSO session at Keycloak and return to the login page. */
export async function beginLogout(idTokenHint?: string): Promise<void> {
  const manager = getUserManager();
  try {
    await manager.signoutRedirect({
      id_token_hint: idTokenHint,
    });
  } catch {
    await clearStoredUser();
    window.location.assign(`${window.location.origin}/login`);
  }
}
